import { useMediaQuery } from "@uidotdev/usehooks";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Link, matchPath, useLocation } from "react-router-dom";
import { logout } from "../../api/auth";
import { useAuth } from "../../contexts/AuthContext";
import logo from "../../logos/sidekick.svg";
import { colors, fonts } from "../../style";
import SidekickMinutesBanner from "../sidekick/SidekickMinutesBanner";
import { GetTheAppsDialog } from "./GetTheApps";
import { useNavItems } from "./navItems";
import { SearchDialog } from "./SearchDialog";
import { Avatar, Icon, Kbd } from "../ui";

export const SIDEBAR_WIDTH = 220;
export const NARROW_QUERY = "(max-width: 860px)";

const isMac =
  typeof navigator !== "undefined" &&
  /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
export const searchShortcut = isMac ? "⌘K" : "Ctrl K";

const style = {
  shell: {
    display: "flex",
    height: "100vh",
    background: colors.pageBackground,
  },
  sidebar: {
    flex: `0 0 ${SIDEBAR_WIDTH}px`,
    width: `${SIDEBAR_WIDTH}px`,
    boxSizing: "border-box",
    background: colors.sidebar,
    borderRight: `1px solid ${colors.border}`,
    padding: "20px 12px 16px",
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    overflowY: "auto",
    fontFamily: fonts.system,
  },
  drawer: {
    position: "fixed",
    top: 0,
    bottom: 0,
    left: 0,
    zIndex: 900,
    boxShadow: "0 0 40px rgba(0, 0, 0, 0.5)",
  },
  brand: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "0 10px 18px",
    fontWeight: 700,
    fontSize: "16px",
    color: colors.text,
    textDecoration: "none",
  },
  main: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    overflowY: "auto",
  },
  topbar: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "10px 16px",
    background: colors.sidebar,
    borderBottom: `1px solid ${colors.border}`,
    fontFamily: fonts.system,
    position: "sticky",
    top: 0,
    zIndex: 10,
  },
  iconButton: {
    border: 0,
    background: "none",
    color: colors.textStrong,
    padding: "6px",
    borderRadius: "8px",
    cursor: "pointer",
    display: "flex",
  },
  navItem: (active) => ({
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "9px 10px",
    borderRadius: "8px",
    color: active ? colors.text : "rgba(255, 255, 255, 0.72)",
    background: active ? colors.raised : "none",
    fontSize: "14px",
    textDecoration: "none",
    border: 0,
    fontFamily: "inherit",
    width: "100%",
    textAlign: "left",
    cursor: "pointer",
  }),
  subItem: (active) => ({
    display: "block",
    padding: "7px 10px 7px 37px",
    borderRadius: "8px",
    fontSize: "13px",
    color: active ? colors.text : "rgba(255, 255, 255, 0.65)",
    background: active ? colors.raised : "none",
    textDecoration: "none",
  }),
  recordCard: {
    display: "block",
    margin: "0 4px 10px",
    padding: "12px",
    borderRadius: "10px",
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    color: "inherit",
    textAlign: "left",
    font: "inherit",
    cursor: "pointer",
  },
};

const pathMatches = (patterns, pathname) =>
  patterns.some((p) => matchPath(`${p}/*`, pathname));

function AppShell({ children }) {
  const { apiUser } = useAuth();
  const { pathname } = useLocation();
  const narrow = useMediaQuery(NARROW_QUERY);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [appsOpen, setAppsOpen] = useState(false);
  const { search } = useNavItems();
  const [searchOpen, setSearchOpen] = useState(false);
  const onSearch = search ? () => setSearchOpen(true) : undefined;

  useEffect(() => {
    if (!search) return;
    const onKey = (e) => {
      // key is undefined on Chrome autofill keydowns
      if ((e.metaKey || e.ctrlKey) && e.key?.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [search]);

  useEffect(() => setDrawerOpen(false), [pathname]);

  const sidebar = (
    <Sidebar
      onSearch={onSearch}
      onGetApps={() => setAppsOpen(true)}
      style={narrow ? { ...style.sidebar, ...style.drawer } : style.sidebar}
    />
  );

  return (
    <div style={style.shell}>
      {narrow
        ? drawerOpen && (
            <>
              <div
                onClick={() => setDrawerOpen(false)}
                style={{
                  position: "fixed",
                  inset: 0,
                  zIndex: 899,
                  background: "rgba(10, 10, 18, 0.6)",
                }}
              />
              {sidebar}
            </>
          )
        : sidebar}

      <div style={style.main}>
        {narrow && (
          <div style={style.topbar}>
            <button
              type="button"
              aria-label="Open menu"
              style={style.iconButton}
              onClick={() => setDrawerOpen(true)}
            >
              <Icon name="menu" size={20} />
            </button>
            <Link to="/" style={{ ...style.brand, padding: 0, flex: 1 }}>
              <img src={logo} alt="" style={{ height: "22px" }} />
              Sidekick
            </Link>
            {onSearch && (
              <button
                type="button"
                aria-label="Search"
                style={style.iconButton}
                onClick={onSearch}
              >
                <Icon name="search" size={18} />
              </button>
            )}
          </div>
        )}
        {apiUser?.has_sidekick && (
          <SidekickMinutesBanner style={{ margin: "12px 16px 0" }} />
        )}
        {/* Grows with the page rather than clipping it, so sticky panels stick */}
        <div
          style={{
            flex: "1 0 auto",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {children}
        </div>
      </div>

      <GetTheAppsDialog open={appsOpen} onClose={() => setAppsOpen(false)} />
      {search && (
        <SearchDialog open={searchOpen} onClose={() => setSearchOpen(false)} />
      )}
    </div>
  );
}

function Sidebar({ onSearch, onGetApps, style: sidebarStyle }) {
  const { pathname } = useLocation();
  const { main, admin, sidekick } = useNavItems();
  const adminActive = pathMatches(
    admin.map((x) => x.to),
    pathname,
  );
  const [adminOpen, setAdminOpen] = useState(adminActive);

  useEffect(() => {
    if (adminActive) setAdminOpen(true);
  }, [adminActive]);

  return (
    <nav aria-label="Main" style={sidebarStyle}>
      <Link to="/" style={style.brand}>
        <img src={logo} alt="" style={{ height: "22px" }} />
        Sidekick
      </Link>

      {main.map((item) => (
        <NavItem key={item.to} item={item} pathname={pathname} />
      ))}

      <div style={{ flex: 1, minHeight: "16px" }} />

      {sidekick && (
        <button
          type="button"
          className="sk-card-button"
          style={style.recordCard}
          onClick={onGetApps}
        >
          <div
            style={{ fontSize: "13px", fontWeight: 600, color: colors.text }}
          >
            Record with Sidekick
          </div>
          <div
            style={{
              fontSize: "12px",
              marginTop: "2px",
              color: colors.textMuted,
            }}
          >
            Get the Mac app
          </div>
        </button>
      )}

      {onSearch && (
        <button
          type="button"
          className="sk-nav"
          onClick={onSearch}
          style={{
            ...style.navItem(false),
            fontSize: "13px",
            color: colors.textMuted,
          }}
        >
          <Icon name="search" size={15} />
          <span style={{ flex: 1 }}>Search</span>
          <Kbd>{searchShortcut}</Kbd>
        </button>
      )}

      {admin.length > 0 && (
        <>
          <button
            type="button"
            className="sk-nav"
            aria-expanded={adminOpen}
            onClick={() => setAdminOpen(!adminOpen)}
            style={style.navItem(adminActive && !adminOpen)}
          >
            <Icon name="settings" size={17} />
            <span style={{ flex: 1 }}>Admin</span>
            <Icon name={adminOpen ? "chevronDown" : "chevronRight"} size={14} />
          </button>
          {adminOpen &&
            admin.map((x) => (
              <SubItem key={x.to} item={x} pathname={pathname} />
            ))}
        </>
      )}

      <UserButton onGetApps={sidekick ? onGetApps : undefined} />
    </nav>
  );
}

function NavItem({ item, pathname }) {
  const active = pathMatches([item.to], pathname);

  return (
    <Link
      to={item.to}
      className="sk-nav"
      aria-current={active ? "page" : undefined}
      style={style.navItem(active)}
    >
      <Icon name={item.icon} size={17} />
      {item.label}
    </Link>
  );
}

function SubItem({ item, pathname }) {
  const active = !!matchPath(`${item.to}/*`, pathname);
  return (
    <Link
      to={item.to}
      className="sk-nav"
      aria-current={active ? "page" : undefined}
      style={style.subItem(active)}
    >
      {item.label}
    </Link>
  );
}

function UserButton({ onGetApps }) {
  const { apiUser, setApiUser, setLoggingOut } = useAuth();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const ref = useRef();
  const name =
    [apiUser?.first_name, apiUser?.last_name].filter(Boolean).join(" ") ||
    apiUser?.email;

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => !ref.current?.contains(e.target) && setOpen(false);
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await logout();
    } finally {
      setApiUser();
      // Cached (and persisted) meetings and calls belong to this user
      queryClient.clear();
      setLoggingOut(false);
    }
  };

  const menuItem = {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    width: "100%",
    padding: "9px 12px",
    border: 0,
    borderRadius: "8px",
    background: "none",
    color: colors.textStrong,
    font: "inherit",
    fontSize: "13px",
    textAlign: "left",
    cursor: "pointer",
  };

  return (
    <div ref={ref} style={{ position: "relative", marginTop: "8px" }}>
      {open && (
        <div
          role="menu"
          style={{
            position: "absolute",
            bottom: "calc(100% + 6px)",
            left: 0,
            right: 0,
            padding: "6px",
            borderRadius: "10px",
            background: colors.dialog,
            border: `1px solid ${colors.borderStrong}`,
            boxShadow: "0 12px 32px rgba(0, 0, 0, 0.45)",
            zIndex: 20,
          }}
        >
          {apiUser?.email && (
            <div
              style={{
                padding: "6px 12px 8px",
                fontSize: "12px",
                color: colors.textMuted,
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {apiUser.email}
            </div>
          )}
          {onGetApps && (
            <button
              type="button"
              role="menuitem"
              className="sk-row"
              style={menuItem}
              onClick={() => {
                setOpen(false);
                onGetApps();
              }}
            >
              <Icon name="download" size={15} />
              Get the Sidekick apps
            </button>
          )}
          <button
            type="button"
            role="menuitem"
            className="sk-row"
            style={menuItem}
            onClick={handleLogout}
          >
            <Icon name="logout" size={15} />
            Log out
          </button>
        </div>
      )}
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        className="sk-card-button"
        onClick={() => setOpen(!open)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          width: "100%",
          padding: "10px",
          borderRadius: "10px",
          border: `1px solid ${colors.border}`,
          background: "none",
          color: "inherit",
          font: "inherit",
          textAlign: "left",
          cursor: "pointer",
        }}
      >
        <Avatar name={name} />
        <span style={{ flex: 1, minWidth: 0 }}>
          <span
            style={ellipsis({
              fontSize: "13px",
              fontWeight: 600,
              color: colors.text,
            })}
          >
            {name}
          </span>
          {apiUser?.server?.name && (
            <span
              style={ellipsis({ fontSize: "12px", color: colors.textMuted })}
            >
              {apiUser.server.name}
            </span>
          )}
        </span>
        <Icon name="chevronDown" size={14} color={colors.textMuted} />
      </button>
    </div>
  );
}

const ellipsis = (s) => ({
  display: "block",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  ...s,
});

export default AppShell;
